import React from 'react';
import { Box, Button, Divider, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TextInput from '../../ui/TextInput';

export type Sex = 'MALE' | 'FEMALE';

export interface FormState {
  firstName: string;
  lastName: string;
  birthDate: string;
  sex: Sex | null;
}

export type FieldErrors = Partial<Record<'firstName' | 'lastName' | 'birthDate' | 'sex', boolean>>;

export interface Step1Props {
  header: React.ReactNode;
  form: FormState;
  errors: FieldErrors;
  onChange: (patch: Partial<FormState>) => void;
  onNext: () => void;
}

export function Step1({ header, form, errors, onChange, onNext }: Step1Props) {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {header}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <StepHeader number={1} label={t('addChild.step1.title')} active />
        <Typography variant="body2" color="text.secondary">
          {t('addChild.step1.description')}
        </Typography>
      </Box>

      <TextInput
        id="child-first-name"
        label={t('addChild.step1.firstNameLabel')}
        placeholder={t('addChild.step1.firstNamePlaceholder')}
        value={form.firstName}
        onChange={(e) => onChange({ firstName: e.target.value })}
        error={errors.firstName}
      />

      <TextInput
        id="child-last-name"
        label={t('addChild.step1.lastNameLabel')}
        placeholder={t('addChild.step1.lastNamePlaceholder')}
        value={form.lastName}
        onChange={(e) => onChange({ lastName: e.target.value })}
        error={errors.lastName}
      />

      <TextInput
        id="child-birth-date"
        label={t('addChild.step1.birthDateLabel')}
        placeholder={t('addChild.step1.birthDatePlaceholder')}
        value={form.birthDate}
        onChange={(e) => onChange({ birthDate: e.target.value })}
        error={errors.birthDate}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
          {t('addChild.step1.sexLabel')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <SexButton
            label={t('addChild.step1.girl')}
            emoji="👧"
            selected={form.sex === 'FEMALE'}
            variant="girl"
            error={errors.sex}
            onClick={() => onChange({ sex: 'FEMALE' })}
          />
          <SexButton
            label={t('addChild.step1.boy')}
            emoji="👦"
            selected={form.sex === 'MALE'}
            variant="boy"
            error={errors.sex}
            onClick={() => onChange({ sex: 'MALE' })}
          />
        </Box>
      </Box>

      <Button
        onClick={onNext}
        fullWidth
        sx={(theme) => ({
          py: 1.75,
          borderRadius: '100px',
          backgroundColor: theme.app.primary,
          color: theme.app.primaryForeground,
          fontWeight: 600,
          fontSize: 16,
          textTransform: 'none',
          '&:hover': { backgroundColor: theme.app.primaryHover },
        })}
      >
        {t('addChild.step1.next')}
      </Button>

      <Divider />

      <StepHeader number={2} label={t('addChild.step2.title')} active={false} />
    </Box>
  );
}

function StepHeader({ number, label, active }: { number: number; label: string; active: boolean }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        sx={(theme) => ({
          width: 28,
          height: 28,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          backgroundColor: active ? theme.app.primary : 'transparent',
          border: active ? 'none' : `2px solid ${theme.app.border}`,
          color: active ? theme.app.primaryForeground : theme.app.mutedForeground,
        })}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1 }}>
          {number}
        </Typography>
      </Box>
      <Typography
        variant="body1"
        sx={(theme) => ({
          fontWeight: active ? 700 : 500,
          color: active ? theme.app.foreground : theme.app.mutedForeground,
        })}
      >
        {label}
      </Typography>
    </Box>
  );
}

function SexButton({
  label,
  emoji,
  selected,
  variant,
  error,
  onClick,
}: {
  label: string;
  emoji: string;
  selected: boolean;
  variant: 'girl' | 'boy';
  error?: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      onClick={onClick}
      sx={(theme) => ({
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        py: 2,
        borderRadius: 2,
        backgroundColor: variant === 'girl' ? theme.app.destructiveSurface : theme.app.infoSurface,
        border: selected
          ? `2px solid ${variant === 'girl' ? theme.app.destructive : theme.app.link}`
          : error
          ? `2px solid ${theme.app.destructive}`
          : '2px solid transparent',
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'border-color 0.15s',
        '&:hover': {
          borderColor: variant === 'girl' ? theme.app.destructive : theme.app.link,
          opacity: 0.9,
        },
      })}
    >
      <Typography sx={{ fontSize: 24, lineHeight: 1 }}>{emoji}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
    </Box>
  );
}
